<template>
  <div class="boleteria">
    <!-- Top Bar -->
    <header class="bol-header">
      <div class="header-left">
        <span class="header-logo">🎫</span>
        <h1 class="header-title">Boletería</h1>
      </div>
      <div class="header-right">
        <button
          v-if="btSupported"
          @click="togglePrinter"
          class="printer-btn"
          :class="{ 'printer-connected': btConnected }"
        >
          {{ btConnected ? '🖨️ ' + btPrinterName : '🔌 Conectar impresora' }}
        </button>
        <router-link to="/mi-cuenta" class="nav-link">👤 Mi cuenta</router-link>
        <router-link to="/admin/dashboard" class="nav-link">📈 Estadísticas</router-link>
        <router-link to="/boleteria/report" class="nav-link">📊 Reporte</router-link>
        <router-link to="/boleteria/scanner" class="nav-link">📷 Escáner</router-link>
        <router-link to="/boleteria/link" class="nav-link">🔗 Vincular</router-link>
        <button @click="logout" class="logout-btn">Salir</button>
      </div>
    </header>

    <!-- Auth check -->
    <div v-if="!token" class="auth-redirect">
      <p>Sesión no iniciada</p>
      <router-link :to="'/login?redirect=/boleteria'" class="login-link">Iniciar sesión →</router-link>
    </div>

    <!-- Main Content -->
    <main v-else class="bol-content">
      <!-- Sale Form -->
      <div class="sale-section">
        <div class="form-card">
          <h2 class="card-title">🎭 Nueva Venta</h2>

          <!-- 1. Función selector -->
          <div class="form-group">
            <label class="form-label">Función</label>
            <select v-model="form.funcion_id" class="form-select" @change="onFuncionChange" :disabled="funciones.length === 0">
              <option :value="null" disabled>{{ funciones.length === 0 ? 'No hay funciones próximas' : 'Seleccionar función...' }}</option>
              <option v-for="f in funciones" :key="f.id" :value="f.id">
                {{ f.name }} — {{ f.date }}
              </option>
            </select>
          </div>

          <!-- 2. Entry Type -->
          <div class="form-group">
            <label class="form-label">Tipo de entrada</label>
            <div class="payment-options">
              <button
                v-for="et in entryTypes"
                :key="et.value"
                @click="onEntryTypeChange(et.value)"
                class="payment-btn"
                :class="{ active: form.entry_type === et.value }"
              >
                {{ et.icon }} {{ et.label }}
              </button>
            </div>
          </div>

          <!-- 3b. Payment Method (hidden for invitation) -->
          <div class="form-group" v-if="form.entry_type !== 'invitation'">
            <label class="form-label">Método de pago</label>
            <div class="payment-options">
              <button
                v-for="pm in cashMethods"
                :key="pm.value"
                @click="form.payment_method = pm.value"
                class="payment-btn"
                :class="{ active: form.payment_method === pm.value }"
              >
                {{ pm.icon }} {{ pm.label }}
              </button>
            </div>
          </div>

          <!-- 3. Quantity -->
          <div class="form-group">
            <label class="form-label">Cantidad</label>
            <div class="qty-control">
              <button @click="form.quantity = Math.max(1, form.quantity - 1)" class="qty-btn">−</button>
              <span class="qty-value">{{ form.quantity }}</span>
              <button @click="form.quantity = Math.min(20, form.quantity + 1)" class="qty-btn">+</button>
            </div>
          </div>

          <!-- 4. Price (auto-calculated, editable for override) -->
          <div class="form-group">
            <label class="form-label">Precio por ticket (UYU)</label>
            <input
              v-model.number="form.price"
              type="number"
              min="0"
              class="form-input"
              :class="{ 'price-auto': priceIsAuto }"
              :disabled="priceIsForced"
            >
            <span v-if="priceIsForced" class="price-note">{{ priceNote }}</span>
          </div>

          <!-- Contact Toggle -->
          <div class="form-group">
            <button @click="showContact = !showContact" class="toggle-contact-btn">
              {{ showContact ? '▼' : '▶' }} Datos del espectador (opcional)
            </button>
          </div>

          <!-- Contact Fields -->
          <div v-if="showContact" class="contact-fields">
            <div class="form-row">
              <div class="form-group half">
                <label class="form-label">Nombre</label>
                <input v-model="form.contact.first_name" class="form-input" placeholder="Nombre">
              </div>
              <div class="form-group half">
                <label class="form-label">Apellido</label>
                <input v-model="form.contact.last_name" class="form-input" placeholder="Apellido">
              </div>
            </div>
            <div class="form-row">
              <div class="form-group half">
                <label class="form-label">Email</label>
                <input v-model="form.contact.email" type="email" class="form-input" placeholder="email@ejemplo.com">
              </div>
              <div class="form-group half">
                <label class="form-label">Teléfono</label>
                <input v-model="form.contact.phone" class="form-input" placeholder="099...">
              </div>
            </div>
            <div class="checkbox-row">
              <label class="checkbox-label">
                <input type="checkbox" v-model="form.contact.wants_news">
                <span>Recibir novedades</span>
              </label>
              <label class="checkbox-label">
                <input type="checkbox" v-model="form.contact.wants_membership">
                <span>Interés en membresía</span>
              </label>
            </div>
          </div>

          <!-- Total -->
          <div class="total-row">
            <span class="total-label">Total:</span>
            <span class="total-value">${{ totalPrice }} UYU</span>
          </div>

          <!-- Sell Button -->
          <button
            @click="sellTickets"
            class="sell-btn"
            :disabled="!canSell || selling"
          >
            {{ selling ? 'Procesando...' : `Vender ${form.quantity} ticket${form.quantity > 1 ? 's' : ''}` }}
          </button>

          <p v-if="error" class="error-msg">{{ error }}</p>
        </div>
      </div>

      <!-- Last Sale Result -->
      <div v-if="lastSale" class="result-section">
        <div class="result-card">
          <div class="result-header">
            <h2 class="card-title">✅ Venta Exitosa</h2>
            <button @click="handlePrint" class="print-btn">🖨️ Imprimir</button>
          </div>
          <p class="result-info">
            {{ lastSale.funcion }} — {{ lastSale.tickets.length }} ticket{{ lastSale.tickets.length > 1 ? 's' : '' }}
            — {{ paymentLabel(lastSale.tickets[0].payment_method) }}
          </p>
          <div class="tickets-grid">
            <div v-for="ticket in lastSale.tickets" :key="ticket.uuid" class="ticket-preview">
              <div class="ticket-qr" :ref="'qr_' + ticket.uuid">
              </div>
              <div class="ticket-info">
                <span class="ticket-func">{{ ticket.funcion_name }}</span>
                <span class="ticket-uuid">{{ ticket.uuid.substring(0, 8) }}...</span>
                <span class="ticket-pm">{{ paymentLabel(ticket.payment_method) }} — ${{ ticket.price }} UYU</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Quick Stats -->
      <div v-if="stats" class="stats-section">
        <div class="stats-card">
          <h2 class="card-title">📊 {{ selectedFuncion ? selectedFuncion.name : 'Todas las funciones' }}</h2>
          <div class="stats-grid">
            <div class="stat-item stat-clickable" @click="toggleHistory">
              <span class="stat-value">{{ stats.summary.total_sold }}</span>
              <span class="stat-label">Vendidos {{ showHistory ? '▼' : '▶' }}</span>
            </div>
            <div class="stat-item stat-rt stat-clickable" @click="toggleRedtickets">
              <span class="stat-value">{{ stats.summary.total_redtickets || 0 }}</span>
              <span class="stat-label">RedTickets {{ showRedtickets ? '▼' : '▶' }}</span>
            </div>
            <div class="stat-item">
              <span class="stat-value">{{ stats.summary.total_used }}</span>
              <span class="stat-label">Ingresados</span>
            </div>
            <div class="stat-item stat-gold">
              <span class="stat-value">${{ stats.summary.total_revenue }}</span>
              <span class="stat-label">Recaudación</span>
            </div>
            <div class="stat-item stat-cash">
              <span class="stat-value">${{ stats.summary.cash_revenue || 0 }}</span>
              <span class="stat-label">💵 Efectivo</span>
            </div>
            <div class="stat-item stat-transfer">
              <span class="stat-value">${{ stats.summary.transfer_revenue || 0 }}</span>
              <span class="stat-label">🏦 Transferencia</span>
            </div>
            <div class="stat-item">
              <span class="stat-value">{{ stats.summary.total_with_contact }}</span>
              <span class="stat-label">Con datos</span>
            </div>
            <div class="stat-item stat-total">
              <span class="stat-value">{{ (stats.summary.total_sold || 0) + (stats.summary.total_redtickets || 0) }}</span>
              <span class="stat-label">Total</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Sales History -->
      <div v-if="showHistory" class="history-section">
        <div class="form-card">
          <h2 class="card-title">🧾 Vendidos en boletería</h2>
          <div v-if="loadingHistory" class="loading-text">Cargando...</div>
          <div v-else-if="history.length === 0" class="empty-text">No hay tickets aún</div>
          <div v-else class="history-list">
            <div
              v-for="(group, idx) in groupedHistory"
              :key="idx"
              class="history-group"
            >
              <div class="history-date">{{ group.label }}</div>
              <div
                v-for="ticket in group.tickets"
                :key="ticket.uuid"
                class="history-item"
                :class="'history-' + ticket.status"
              >
                <div class="history-item-left">
                  <span class="history-func">{{ ticket.funcion_name }}</span>
                  <span class="history-meta">
                    {{ paymentLabel(ticket.payment_method) }} — ${{ ticket.price }} UYU
                    <span v-if="ticket.contact"> — {{ ticket.contact.first_name }} {{ ticket.contact.last_name }}</span>
                  </span>
                  <span class="history-uuid">{{ ticket.uuid.substring(0, 8) }}...</span>
                </div>
                <div class="history-item-right">
                  <button
                    v-if="ticket.status === 'valid' && ticket.payment_method !== 'redtickets'"
                    @click="confirmMarkUsed(ticket)"
                    class="use-ticket-btn"
                    title="Marcar como usado"
                  >✅</button>
                  <button
                    v-if="ticket.status === 'valid'"
                    @click="confirmCancel(ticket)"
                    class="cancel-ticket-btn"
                    title="Cancelar ticket"
                  >❌</button>
                  <button
                    @click="confirmDelete(ticket)"
                    class="delete-ticket-btn"
                    title="Eliminar ticket"
                  >🗑️</button>
                  <span class="history-status">{{ statusLabel(ticket.status) }}</span>
                  <span class="history-time">{{ formatTime(ticket.created_at) }}</span>
                  <button @click="handleReprint(ticket)" class="reprint-btn" title="Reimprimir">🖨️</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- RedTickets History -->
      <div v-if="showRedtickets && groupedRedtickets.length" class="history-section">
        <div class="form-card">
          <h2 class="card-title">🎟️ Importados RedTickets</h2>
          <div class="history-list">
            <div v-for="(group, idx) in groupedRedtickets" :key="idx" class="history-group">
              <div class="history-date">{{ group.label }}</div>
              <div
                v-for="ticket in group.tickets"
                :key="ticket.uuid"
                class="history-item"
                :class="'history-' + ticket.status"
              >
                <div class="history-item-left">
                  <span class="history-func">{{ ticket.funcion_name }}</span>
                  <span class="history-meta">
                    RedTickets
                    <span v-if="ticket.contact"> — {{ ticket.contact.first_name }} {{ ticket.contact.last_name }}</span>
                  </span>
                  <span class="history-uuid">{{ ticket.uuid.substring(0, 8) }}...</span>
                </div>
                <div class="history-item-right">
                  <span class="history-status">{{ statusLabel(ticket.status) }}</span>
                  <span class="history-time">{{ formatTime(ticket.created_at) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- RedTickets Import -->
      <div class="import-section">
        <div class="form-card">
          <button @click="showImport = !showImport" class="toggle-contact-btn">
            {{ showImport ? '▼' : '▶' }} 🎟️ Importar RedTickets
          </button>

          <div v-if="showImport" class="import-content">
            <p class="import-desc">Subí el CSV descargado de RedTickets para importar tickets y datos de contacto.</p>

            <div class="form-group">
              <label class="form-label">Función</label>
              <select v-model="importForm.funcion_id" class="form-select">
                <option :value="null" disabled>Seleccionar función...</option>
                <option v-for="f in funciones" :key="f.id" :value="f.id">{{ f.name }}</option>
              </select>
            </div>

            <div class="form-group">
              <label class="form-label">Precio por ticket (UYU)</label>
              <input v-model.number="importForm.price" type="number" min="0" class="form-input" placeholder="0">
            </div>

            <div class="form-group">
              <label class="form-label">Archivo CSV</label>
              <input type="file" ref="csvFile" accept=".csv" class="form-input file-input" @change="onFileSelected">
              <span v-if="importForm.fileName" class="file-name">{{ importForm.fileName }}</span>
            </div>

            <button
              @click="importRedTickets"
              class="import-btn"
              :disabled="!canImport || importing"
            >
              {{ importing ? 'Importando...' : 'Importar tickets' }}
            </button>

            <!-- Import result -->
            <div v-if="importResult" class="import-result" :class="importResult.success ? 'import-success' : 'import-error'">
              <div v-if="importResult.success">
                <p class="import-result-title">✅ {{ importResult.message }}</p>
                <p class="import-result-detail">{{ importResult.funcion }}</p>
                <p class="import-result-detail">
                  Tickets: {{ importResult.stats.tickets_created }} nuevos, {{ importResult.stats.tickets_updated }} actualizados
                </p>
                <p class="import-result-detail">
                  Contactos: {{ importResult.stats.contacts_created }} nuevos, {{ importResult.stats.contacts_updated }} actualizados
                </p>
              </div>
              <div v-else>
                <p class="import-result-title">❌ {{ importResult.error }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>

    <!-- Hidden print area -->
    <div ref="printArea" class="print-area">
      <template v-if="lastSale">
        <div v-for="ticket in lastSale.tickets" :key="'print_' + ticket.uuid" class="print-ticket">
        <div class="print-header">GRUPO BABILONIA</div>
        <div class="print-festival">Festival Arena y Barro</div>
        <div class="print-func">{{ ticket.funcion_name }}</div>
        <div class="print-qr" :ref="'printqr_' + ticket.uuid">
        </div>
        <div class="print-uuid">{{ ticket.uuid }}</div>
        <div class="print-details">
          <span>{{ paymentLabel(ticket.payment_method) }}</span>
          <span>${{ ticket.price }} UYU</span>
        </div>
        <div class="print-date">{{ formatDate(ticket.created_at) }}</div>
        <div class="print-cut">- - - - - - - - - - - - - - - - - -</div>
      </div>
      </template>
    </div>
  </div>
</template>

<script>
import printer from '@/utils/bluetooth-printer.js'

const API_BASE = '/api'

export default {
  name: 'BoleteriaPage',
  data() {
    return {
      token: localStorage.getItem('babilonia_auth_token') || sessionStorage.getItem('admin_token') || null,
      funciones: [],
      form: {
        funcion_id: null,
        quantity: 1,
        payment_method: 'cash',
        entry_type: 'normal',
        price: 0,
        contact: {
          first_name: '',
          last_name: '',
          email: '',
          phone: '',
          wants_news: false,
          wants_membership: false,
        }
      },
      showContact: false,
      entryTypes: [
        { value: 'normal', label: 'Normal', icon: '🎭' },
        { value: 'invitation', label: 'Invitación', icon: '🎁' },
        { value: 'combo', label: 'Combo', icon: '📦' },
        { value: '2x1', label: '2 por 1', icon: '🤝' },
      ],
      cashMethods: [
        { value: 'cash', label: 'Efectivo', icon: '💵' },
        { value: 'transfer', label: 'Transferencia', icon: '🏦' },
      ],
      selling: false,
      error: '',
      lastSale: null,
      stats: null,
      qrLibLoaded: false,
      showHistory: false,
      showRedtickets: false,
      history: [],
      loadingHistory: false,
      // Bluetooth printer
      btSupported: false,
      btConnected: false,
      btPrinterName: '',
      btLog: '',
      // RedTickets import
      showImport: false,
      importing: false,
      importResult: null,
      importForm: {
        funcion_id: null,
        price: 0,
        file: null,
        fileName: '',
      },
    }
  },
  computed: {
    canSell() {
      return this.form.funcion_id && this.form.entry_type && this.form.quantity > 0 && (this.form.entry_type === 'invitation' || this.form.payment_method)
    },
    canImport() {
      return this.importForm.funcion_id && this.importForm.file
    },
    selectedFuncion() {
      if (!this.form.funcion_id) return null
      return this.funciones.find(f => f.id === this.form.funcion_id) || null
    },
    priceIsForced() {
      return this.form.entry_type === 'invitation'
    },
    priceIsAuto() {
      return this.form.entry_type === '2x1'
    },
    priceNote() {
      if (this.form.entry_type === 'invitation') return 'Invitación: sin cargo'
      if (this.form.entry_type === '2x1') return 'Precio 2x1 — mitad del precio normal'
      return ''
    },
    totalPrice() {
      return (this.form.price || 0) * this.form.quantity
    },
    groupedHistory() {
      if (!this.history.length) return []
      const groups = {}
      this.history.filter(t => t.payment_method !== 'redtickets').forEach(t => {
        const utcIso = t.created_at && !t.created_at.endsWith('Z') ? t.created_at + 'Z' : t.created_at
        const date = new Date(utcIso)
        const key = date.toLocaleDateString('es-UY', { weekday: 'long', day: 'numeric', month: 'long' })
        if (!groups[key]) groups[key] = { label: key, tickets: [] }
        groups[key].tickets.push(t)
      })
      return Object.values(groups)
    },
    groupedRedtickets() {
      if (!this.history.length) return []
      const groups = {}
      this.history.filter(t => t.payment_method === 'redtickets').forEach(t => {
        const utcIso = t.created_at && !t.created_at.endsWith('Z') ? t.created_at + 'Z' : t.created_at
        const date = new Date(utcIso)
        const key = date.toLocaleDateString('es-UY', { weekday: 'long', day: 'numeric', month: 'long' })
        if (!groups[key]) groups[key] = { label: key, tickets: [] }
        groups[key].tickets.push(t)
      })
      return Object.values(groups)
    }
  },
  async mounted() {
    if (!this.token) return

    // Check if user has boleteria access
    const userStr = localStorage.getItem('babilonia_auth_user')
    if (userStr) {
      try {
        const user = JSON.parse(userStr)
        const hasAccess = user.role === 'admin' ||
          (user.permissions || []).includes('boleteria')
        if (!hasAccess) {
          this.$router.replace('/mi-cuenta')
          return
        }
      } catch (e) { /* ignore parse errors */ }
    }
    this.btSupported = printer.isSupported()
    this.btConnected = printer.connected
    await this.loadQRLib()
    await this.fetchFunciones()
    await this.fetchStats()
  },
  methods: {
    // ─── Bluetooth Printer ─────────────────────
    async togglePrinter() {
      if (this.btConnected) {
        await printer.disconnect()
        this.btConnected = false
        this.btPrinterName = ''
        this.btLog = ''
      } else {
        try {
          this.btLog = 'Conectando...'
          const ok = await printer.connect()
          if (ok) {
            this.btConnected = true
            this.btPrinterName = printer.printerName
            this.btLog = '✅ Impresora lista!'
          } else {
            this.btLog = 'Cancelado'
          }
        } catch (err) {
          if (this.btConnected && err.message && err.message.includes('cancelled')) {
            return
          }
          this.error = 'Error: ' + err.message
          this.btLog = 'Error: ' + err.message
        }
      }
    },

    // ─── Form Logic ─────────────────────────────
    onFuncionChange() {
      // Auto-fill price from función data
      const funcion = this.selectedFuncion
      if (funcion && funcion.price) {
        this.applyPriceLogic(funcion.price)
      }
      // Refresh stats and history for this función
      this.history = []
      this.showRedtickets = false
      this.fetchStats()
      if (this.showHistory) this.fetchHistory()
    },

    onEntryTypeChange(entryType) {
      this.form.entry_type = entryType
      // Invitation doesn't need payment method
      if (entryType === 'invitation') {
        this.form.payment_method = 'cash' // reset but hidden
      }
      const basePrice = this.selectedFuncion ? this.selectedFuncion.price : 0
      this.applyPriceLogic(basePrice)
    },

    applyPriceLogic(basePrice) {
      const method = this.form.entry_type
      if (method === 'invitation') {
        this.form.price = 0
      } else if (method === 'combo') {
        this.form.price = 300
      } else if (method === '2x1') {
        this.form.price = Math.round(basePrice / 2)
      } else {
        this.form.price = basePrice
      }
    },

    handlePrint() {
      if (!this.lastSale) return
      if (this.btConnected) {
        this.btPrintTickets(this.lastSale.tickets)
      } else {
        this.printTickets()
      }
    },

    handleReprint(ticket) {
      if (this.btConnected) {
        this.btReprintTicket(ticket)
      } else {
        this.reprintTicket(ticket)
      }
    },

    async confirmMarkUsed(ticket) {
      const uuid = ticket.uuid.substring(0, 8)
      if (!confirm(`¿Marcar el ticket ${uuid}... como usado manualmente?`)) return
      try {
        const res = await fetch(`${API_BASE}/tickets/use/${ticket.uuid}`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${this.token}` }
        })
        if (res.ok) {
          ticket.status = 'used'
          ticket.used_at = new Date().toISOString()
          await this.fetchStats()
        } else {
          const data = await res.json()
          alert(data.error || 'Error al marcar ticket')
        }
      } catch (e) {
        alert('Error de conexión')
      }
    },

    async confirmCancel(ticket) {
      const funcName = ticket.funcion_name
      const uuid = ticket.uuid.substring(0, 8)
      const ok = confirm(`¿Cancelar ticket ${uuid}... de "${funcName}"?\n\nEsta acción no se puede deshacer.`)
      if (!ok) return
      await this.cancelTicket(ticket)
    },

    async cancelTicket(ticket) {
      try {
        const res = await fetch(`${API_BASE}/tickets/cancel/${ticket.uuid}`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${this.token}` },
        })
        if (res.status === 401) { this.token = null; return }
        const data = await res.json()
        if (res.ok) {
          ticket.status = 'cancelled'
          this.fetchStats()
        } else {
          this.error = data.error || 'Error al cancelar'
        }
      } catch {
        this.error = 'Error de conexión'
      }
    },

    async confirmDelete(ticket) {
      const uuid = ticket.uuid.substring(0, 8)
      const ok = confirm(`¿ELIMINAR ticket ${uuid}...?\n\nEsto borra el ticket permanentemente de la base de datos.`)
      if (!ok) return
      await this.deleteTicket(ticket)
    },

    async deleteTicket(ticket) {
      try {
        const res = await fetch(`${API_BASE}/tickets/delete/${ticket.uuid}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${this.token}` },
        })
        if (res.status === 401) { this.token = null; return }
        const data = await res.json()
        if (res.ok) {
          // Remove from history list
          this.history = this.history.filter(t => t.uuid !== ticket.uuid)
          this.fetchStats()
        } else {
          this.error = data.error || 'Error al eliminar'
        }
      } catch {
        this.error = 'Error de conexión'
      }
    },

    async btPrintTickets(tickets) {
      for (let i = 0; i < tickets.length; i++) {
        try {
          await printer.printTicket({
            funcion: tickets[i].funcion_name,
            uuid: tickets[i].uuid,
            payment: this.paymentLabel(tickets[i].payment_method),
            price: tickets[i].price,
            date: this.formatDate(tickets[i].created_at),
          })
          // Wait between tickets so printer can finish
          if (i < tickets.length - 1) {
            await new Promise(r => setTimeout(r, 3000))
          }
        } catch (err) {
          console.error('BT print error:', err)
          this.btConnected = false
          this.error = 'Error de impresora. Usá el botón 🖨️ para reimprimir.'
          break
        }
      }
    },

    async btReprintTicket(ticket) {
      try {
        await printer.printTicket({
          funcion: ticket.funcion_name,
          uuid: ticket.uuid,
          payment: this.paymentLabel(ticket.payment_method),
          price: ticket.price,
          date: this.formatDate(ticket.created_at),
        })
      } catch (err) {
        this.error = 'Error de impresora: ' + err.message
        this.btConnected = false
      }
    },

    async loadQRLib() {
      if (window.QRCode) {
        this.qrLibLoaded = true
        return
      }
      return new Promise((resolve) => {
        const script = document.createElement('script')
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/qrcodejs/1.0.0/qrcode.min.js'
        script.onload = () => {
          this.qrLibLoaded = true
          resolve()
        }
        document.head.appendChild(script)
      })
    },

    async fetchFunciones() {
      try {
        const res = await fetch(`${API_BASE}/tickets/funciones`, {
          headers: { 'Authorization': `Bearer ${this.token}` }
        })
        if (res.status === 401) { this.token = null; return }
        const data = await res.json()
        this.funciones = data.funciones
        // Auto-select the next upcoming función (first in the list, sorted by date)
        if (this.funciones.length > 0 && !this.form.funcion_id) {
          const sorted = [...this.funciones].sort((a, b) => a.date.localeCompare(b.date))
          this.form.funcion_id = sorted[0].id
          this.onFuncionChange()
        }
      } catch (e) {
        this.error = 'Error cargando funciones'
      }
    },

    async fetchStats() {
      try {
        let url = `${API_BASE}/tickets/report`
        if (this.form.funcion_id) {
          url += `?funcion_id=${this.form.funcion_id}`
        }
        const res = await fetch(url, {
          headers: { 'Authorization': `Bearer ${this.token}` }
        })
        if (res.ok) {
          this.stats = await res.json()
        }
      } catch { /* silent */ }
    },

    async sellTickets() {
      this.error = ''
      this.selling = true

      // Derive backend payment_method from entry_type + payment_method
      let effectiveMethod = this.form.payment_method  // 'cash' or 'transfer'
      if (this.form.entry_type === 'invitation') effectiveMethod = 'invitation'
      else if (this.form.entry_type === 'combo') effectiveMethod = this.form.payment_method // cash or transfer
      else if (this.form.entry_type === '2x1') effectiveMethod = this.form.payment_method  // cash or transfer (price already halved)

      const body = {
        funcion_id: this.form.funcion_id,
        quantity: this.form.quantity,
        payment_method: effectiveMethod,
        price: this.form.price || 0,
      }

      // Include contact if name is provided
      if (this.form.contact.first_name && this.form.contact.last_name) {
        body.contact = { ...this.form.contact }
      }

      try {
        const res = await fetch(`${API_BASE}/tickets/sell`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${this.token}`,
          },
          body: JSON.stringify(body)
        })

        if (res.status === 401) { this.token = null; return }

        const data = await res.json()
        if (res.ok) {
          this.lastSale = data
          // Reset form (keep función and price)
          this.form.quantity = 1
          this.form.entry_type = 'normal'
          this.form.payment_method = 'cash'
          this.form.contact = {
            first_name: '', last_name: '', email: '', phone: '',
            wants_news: false, wants_membership: false,
          }
          this.showContact = false
          // Generate QR codes after DOM update
          this.$nextTick(() => this.generateQRCodes())
          // Auto-print via Bluetooth if connected
          if (this.btConnected) {
            this.btPrintTickets(data.tickets)
          }
          // Refresh stats and history
          this.fetchStats()
          if (this.showHistory) this.fetchHistory()
        } else {
          this.error = data.error || 'Error al vender'
        }
      } catch {
        this.error = 'Error de conexión'
      }
      this.selling = false
    },

    generateQRCodes() {
      if (!this.lastSale || !window.QRCode) return

      this.lastSale.tickets.forEach(ticket => {
        // Screen preview QR
        const screenRef = this.$refs['qr_' + ticket.uuid]
        const screenEl = Array.isArray(screenRef) ? screenRef[0] : screenRef
        if (screenEl) {
          screenEl.innerHTML = ''
          new window.QRCode(screenEl, {
            text: ticket.uuid,
            width: 120,
            height: 120,
            colorDark: '#000000',
            colorLight: '#ffffff',
            correctLevel: window.QRCode.CorrectLevel.M,
          })
        }

        // Print QR (larger)
        const printRef = this.$refs['printqr_' + ticket.uuid]
        const printEl = Array.isArray(printRef) ? printRef[0] : printRef
        if (printEl) {
          printEl.innerHTML = ''
          new window.QRCode(printEl, {
            text: ticket.uuid,
            width: 180,
            height: 180,
            colorDark: '#000000',
            colorLight: '#ffffff',
            correctLevel: window.QRCode.CorrectLevel.M,
          })
        }
      })
    },

    printTickets() {
      // Clone print area and convert canvas → img so QR survives innerHTML copy
      const printArea = this.$refs.printArea.cloneNode(true)
      const origCanvases = this.$refs.printArea.querySelectorAll('canvas')
      const cloneCanvases = printArea.querySelectorAll('canvas')
      cloneCanvases.forEach((canvas, i) => {
        const img = document.createElement('img')
        img.src = origCanvases[i].toDataURL('image/png')
        img.style.width = origCanvases[i].width + 'px'
        img.style.height = origCanvases[i].height + 'px'
        canvas.parentElement.replaceChild(img, canvas)
      })

      const printContent = printArea.innerHTML
      const win = window.open('', '_blank', 'width=300,height=600')
      win.document.write(`
        <html>
        <head>
          <title>Tickets</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: 'Courier New', monospace; width: 58mm; }
            .print-ticket {
              padding: 8px 4px;
              text-align: center;
              page-break-after: always;
            }
            .print-header {
              font-size: 16px;
              font-weight: bold;
              margin-bottom: 2px;
            }
            .print-festival {
              font-size: 10px;
              margin-bottom: 8px;
              color: #555;
            }
            .print-func {
              font-size: 14px;
              font-weight: bold;
              margin-bottom: 8px;
              border-top: 1px dashed #000;
              border-bottom: 1px dashed #000;
              padding: 6px 0;
            }
            .print-qr {
              margin: 8px auto;
              display: flex;
              justify-content: center;
            }
            .print-qr img, .print-qr canvas {
              width: 180px !important;
              height: 180px !important;
            }
            .print-uuid {
              font-size: 7px;
              color: #666;
              margin: 4px 0;
              word-break: break-all;
            }
            .print-details {
              font-size: 11px;
              display: flex;
              justify-content: space-between;
              padding: 4px 8px;
            }
            .print-date {
              font-size: 9px;
              color: #666;
              margin-top: 4px;
            }
            .print-cut {
              margin-top: 8px;
              font-size: 10px;
              color: #999;
            }
          </style>
        </head>
        <body>${printContent}</body>
        </html>
      `)
      win.document.close()
      setTimeout(() => { win.print(); }, 500)
    },

    paymentLabel(method) {
      const labels = {
        cash: 'Efectivo',
        transfer: 'Transferencia',
        redtickets: 'RedTickets',
        invitation: 'Invitación',
        combo: 'Combo',
        '2x1': '2 por 1',
        'cash_2x1': 'Efectivo 2x1',
        'transfer_2x1': 'Transferencia 2x1',
      }
      return labels[method] || method
    },

    statusLabel(status) {
      return { valid: 'Válido', used: 'Utilizado', cancelled: 'Cancelado' }[status] || status
    },

    formatDate(iso) {
      if (!iso) return ''
      // Server stores UTC — append Z if no timezone indicator
      const utcIso = iso.includes('+') || iso.endsWith('Z') ? iso : iso + 'Z'
      const d = new Date(utcIso)
      return d.toLocaleString('es-UY', { dateStyle: 'short', timeStyle: 'short' })
    },

    formatTime(iso) {
      if (!iso) return ''
      const utcIso = iso.includes('+') || iso.endsWith('Z') ? iso : iso + 'Z'
      const d = new Date(utcIso)
      return d.toLocaleString('es-UY', { timeStyle: 'short' })
    },

    // ─── RedTickets Import ─────────────────────
    onFileSelected(event) {
      const file = event.target.files[0]
      if (file) {
        this.importForm.file = file
        this.importForm.fileName = file.name
      }
    },

    async importRedTickets() {
      if (!this.canImport) return
      this.importing = true
      this.importResult = null
      this.error = ''

      const formData = new FormData()
      formData.append('file', this.importForm.file)
      formData.append('funcion_id', this.importForm.funcion_id)
      formData.append('price', this.importForm.price || 0)

      try {
        const res = await fetch(`${API_BASE}/tickets/import-redtickets`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${this.token}` },
          body: formData,
        })
        if (res.status === 401) { this.token = null; return }
        const data = await res.json()
        if (res.ok) {
          this.importResult = { success: true, ...data }
          // Reset file input
          this.importForm.file = null
          this.importForm.fileName = ''
          if (this.$refs.csvFile) this.$refs.csvFile.value = ''
          // Switch sale form to the imported función so stats update
          this.form.funcion_id = this.importForm.funcion_id
          this.onFuncionChange()
        } else {
          this.importResult = { success: false, error: data.error || 'Error al importar' }
        }
      } catch {
        this.importResult = { success: false, error: 'Error de conexión' }
      }
      this.importing = false
    },

    async toggleRedtickets() {
      this.showRedtickets = !this.showRedtickets
      if (this.showRedtickets && this.history.length === 0) {
        await this.fetchHistory()
      }
    },

    async toggleHistory() {
      this.showHistory = !this.showHistory
      if (this.showHistory && this.history.length === 0) {
        await this.fetchHistory()
      }
    },

    async fetchHistory() {
      this.loadingHistory = true
      try {
        let url = `${API_BASE}/tickets/history?limit=100`
        if (this.form.funcion_id) {
          url += `&funcion_id=${this.form.funcion_id}`
        }
        const res = await fetch(url, {
          headers: { 'Authorization': `Bearer ${this.token}` }
        })
        if (res.ok) {
          const data = await res.json()
          this.history = data.tickets
        }
      } catch { /* silent */ }
      this.loadingHistory = false
    },

    reprintTicket(ticket) {
      // Build a single-ticket reprint using a new window
      if (!window.QRCode) return

      const win = window.open('', '_blank', 'width=300,height=600')
      win.document.write(`
        <html>
        <head>
          <title>Ticket</title>
          <script src="https://cdnjs.cloudflare.com/ajax/libs/qrcodejs/1.0.0/qrcode.min.js"></` + `script>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: 'Courier New', monospace; width: 58mm; text-align: center; padding: 8px 4px; }
            .print-header { font-size: 16px; font-weight: bold; margin-bottom: 2px; }
            .print-festival { font-size: 10px; margin-bottom: 8px; color: #555; }
            .print-func { font-size: 14px; font-weight: bold; margin-bottom: 8px; border-top: 1px dashed #000; border-bottom: 1px dashed #000; padding: 6px 0; }
            .print-qr { margin: 8px auto; display: flex; justify-content: center; }
            .print-uuid { font-size: 7px; color: #666; margin: 4px 0; word-break: break-all; }
            .print-details { font-size: 11px; display: flex; justify-content: space-between; padding: 4px 8px; }
            .print-date { font-size: 9px; color: #666; margin-top: 4px; }
          </style>
        </head>
        <body>
          <div class="print-header">GRUPO BABILONIA</div>
          <div class="print-festival">Festival Arena y Barro</div>
          <div class="print-func">${ticket.funcion_name}</div>
          <div class="print-qr" id="qr"></div>
          <div class="print-uuid">${ticket.uuid}</div>
          <div class="print-details">
            <span>${this.paymentLabel(ticket.payment_method)}</span>
            <span>$${ticket.price} UYU</span>
          </div>
          <div class="print-date">${this.formatDate(ticket.created_at)}</div>
          <script>
            new QRCode(document.getElementById('qr'), {
              text: '${ticket.uuid}',
              width: 180, height: 180,
              colorDark: '#000000', colorLight: '#ffffff',
              correctLevel: QRCode.CorrectLevel.M,
            });
            setTimeout(function() { window.print(); }, 600);
          </` + `script>
        </body>
        </html>
      `)
      win.document.close()
    },

    logout() {
      sessionStorage.removeItem('admin_token')
      localStorage.removeItem('babilonia_auth_token')
      localStorage.removeItem('babilonia_auth_user')
      this.$router.push('/login?redirect=/boleteria')
    }
  }
}
</script>

<style scoped>
.boleteria {
  min-height: 100vh;
  background: #0a0a0a;
  color: #e0e0e0;
}

/* ─── Header ─────────────────────── */
.bol-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 24px;
  background: #111;
  border-bottom: 1px solid #222;
  flex-wrap: wrap;
  gap: 10px;
}
.header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}
.header-logo { font-size: 28px; }
.header-title {
  font-size: 20px;
  color: #c9a227;
  margin: 0;
}
.header-right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.nav-link {
  color: #888;
  text-decoration: none;
  font-size: 14px;
  padding: 8px 12px;
  border-radius: 6px;
  transition: all 0.2s;
  white-space: nowrap;
}
.nav-link:hover { color: #c9a227; background: #1a1a1a; }
.printer-btn {
  background: #1a1a1a;
  border: 1px solid #333;
  color: #aaa;
  padding: 6px 12px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.2s;
}
.printer-btn:hover { border-color: #c9a227; color: #c9a227; }
.printer-connected {
  border-color: #2ecc71;
  color: #2ecc71;
  background: #0a1a0a;
}
.logout-btn {
  background: #333;
  color: #aaa;
  border: none;
  padding: 6px 14px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
}
.logout-btn:hover { background: #444; color: #fff; }

/* ─── Auth Redirect ──────────────── */
.auth-redirect {
  text-align: center;
  padding: 100px 20px;
  color: #666;
}
.login-link {
  display: inline-block;
  margin-top: 15px;
  color: #c9a227;
  text-decoration: none;
  font-size: 16px;
}

/* ─── Content Layout ─────────────── */
.bol-content {
  max-width: 900px;
  margin: 0 auto;
  padding: 24px 16px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

/* ─── Cards ──────────────────────── */
.form-card, .result-card, .stats-card {
  background: #111;
  border: 1px solid #222;
  border-radius: 12px;
  padding: 28px 24px;
}
.card-title {
  font-size: 18px;
  color: #c9a227;
  margin: 0 0 20px;
}

/* ─── Form ───────────────────────── */
.form-group {
  margin-bottom: 18px;
}
.form-label {
  display: block;
  font-size: 13px;
  color: #888;
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.form-select, .form-input {
  width: 100%;
  padding: 12px 14px;
  background: #0a0a0a;
  border: 1px solid #333;
  border-radius: 8px;
  color: #fff;
  font-size: 15px;
  outline: none;
  transition: border-color 0.2s;
  box-sizing: border-box;
}
.form-select:focus, .form-input:focus {
  border-color: #c9a227;
}
.form-select option {
  background: #111;
}
.price-auto {
  color: #c9a227;
}
.form-input:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background: #0d0d0d;
}
.price-note {
  display: block;
  font-size: 12px;
  color: #c9a227;
  margin-top: 4px;
  font-style: italic;
}

/* Quantity */
.qty-control {
  display: flex;
  align-items: center;
  gap: 0;
}
.qty-btn {
  width: 48px;
  height: 48px;
  background: #1a1a1a;
  border: 1px solid #333;
  color: #c9a227;
  font-size: 22px;
  cursor: pointer;
  transition: all 0.2s;
}
.qty-btn:first-child { border-radius: 8px 0 0 8px; }
.qty-btn:last-child { border-radius: 0 8px 8px 0; }
.qty-btn:hover { background: #222; }
.qty-value {
  width: 60px;
  height: 48px;
  line-height: 48px;
  text-align: center;
  background: #0a0a0a;
  border-top: 1px solid #333;
  border-bottom: 1px solid #333;
  font-size: 20px;
  font-weight: bold;
  color: #fff;
}

/* Payment */
.payment-options {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}
.payment-btn {
  flex: 1;
  min-width: 120px;
  padding: 14px 16px;
  background: #1a1a1a;
  border: 2px solid #333;
  border-radius: 10px;
  color: #aaa;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  text-align: center;
}
.payment-btn:hover { border-color: #555; color: #fff; }
.payment-btn.active {
  border-color: #c9a227;
  color: #c9a227;
  background: #1a1800;
}

/* Contact toggle */
.toggle-contact-btn {
  background: none;
  border: none;
  color: #888;
  font-size: 14px;
  cursor: pointer;
  padding: 0;
}
.toggle-contact-btn:hover { color: #c9a227; }

.contact-fields {
  background: #0d0d0d;
  border: 1px solid #222;
  border-radius: 8px;
  padding: 16px;
  margin-top: -8px;
}
.form-row {
  display: flex;
  gap: 12px;
}
.form-group.half {
  flex: 1;
}
.checkbox-row {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
}
.checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #aaa;
  font-size: 14px;
  cursor: pointer;
}
.checkbox-label input[type="checkbox"] {
  accent-color: #c9a227;
  width: 16px;
  height: 16px;
}

/* Total */
.total-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;
  border-top: 1px solid #222;
  margin-top: 10px;
}
.total-label { font-size: 16px; color: #888; }
.total-value { font-size: 24px; font-weight: bold; color: #c9a227; }

/* Sell button */
.sell-btn {
  width: 100%;
  padding: 16px;
  background: #c9a227;
  color: #000;
  font-size: 18px;
  font-weight: bold;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.sell-btn:hover { background: #e0b830; }
.sell-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.error-msg {
  color: #e74c3c;
  text-align: center;
  margin-top: 10px;
  font-size: 14px;
}

/* ─── Result ─────────────────────── */
.result-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}
.result-header .card-title { margin: 0; }
.print-btn {
  background: #222;
  border: 1px solid #333;
  color: #c9a227;
  padding: 8px 16px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
}
.print-btn:hover { background: #333; }

.result-info {
  color: #aaa;
  font-size: 14px;
  margin: 0 0 16px;
}

.tickets-grid {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}
.ticket-preview {
  background: #0a0a0a;
  border: 1px solid #222;
  border-radius: 10px;
  padding: 16px;
  text-align: center;
  min-width: 160px;
  flex: 1;
}
.ticket-qr {
  margin: 0 auto 10px;
  display: flex;
  justify-content: center;
}
.ticket-info {
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.ticket-func { font-size: 13px; color: #c9a227; font-weight: bold; }
.ticket-uuid { font-size: 11px; color: #555; font-family: monospace; }
.ticket-pm { font-size: 12px; color: #888; }

/* ─── Stats ──────────────────────── */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
}
.stat-item {
  text-align: center;
  padding: 16px 8px;
  background: #0a0a0a;
  border-radius: 8px;
  border: 1px solid #1a1a1a;
}
.stat-value {
  display: block;
  font-size: 28px;
  font-weight: bold;
  color: #c9a227;
}
.stat-label {
  display: block;
  font-size: 12px;
  color: #666;
  margin-top: 4px;
  text-transform: uppercase;
}

/* ─── RedTickets Import ──────────── */
.import-content {
  margin-top: 16px;
}
.import-desc {
  color: #888;
  font-size: 13px;
  margin: 0 0 16px;
}
.file-input {
  padding: 10px !important;
  cursor: pointer;
}
.file-input::-webkit-file-upload-button {
  background: #222;
  color: #c9a227;
  border: 1px solid #444;
  border-radius: 6px;
  padding: 6px 14px;
  cursor: pointer;
  margin-right: 10px;
}
.file-name {
  display: block;
  font-size: 12px;
  color: #666;
  margin-top: 4px;
}
.import-btn {
  width: 100%;
  padding: 14px;
  background: #f39c12;
  color: #000;
  font-size: 16px;
  font-weight: bold;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.import-btn:hover { background: #e67e22; }
.import-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.import-result {
  margin-top: 16px;
  padding: 16px;
  border-radius: 8px;
}
.import-success {
  background: #0a2a0a;
  border: 1px solid #2ecc71;
}
.import-error {
  background: #2a0a0a;
  border: 1px solid #e74c3c;
}
.import-result-title {
  font-size: 16px;
  font-weight: bold;
  margin: 0 0 8px;
}
.import-result-detail {
  font-size: 13px;
  color: #aaa;
  margin: 2px 0;
}

/* ─── Stats clickable ─────────────── */
.stat-clickable {
  cursor: pointer;
  transition: all 0.2s;
}
.stat-clickable:hover {
  background: #151515;
  border-color: #c9a227;
}
.stat-rt .stat-value {
  color: #e67e22;
}
.stat-gold .stat-value {
  color: #c9a227;
}
.stat-cash .stat-value {
  color: #2ecc71;
}
.stat-transfer .stat-value {
  color: #3498db;
}
.stat-total .stat-value {
  color: #2ecc71;
}

/* ─── History ────────────────────── */
.loading-text, .empty-text {
  text-align: center;
  color: #666;
  padding: 20px;
  font-size: 14px;
}
.history-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.history-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.history-date {
  font-size: 12px;
  color: #c9a227;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 4px 0;
  border-bottom: 1px solid #1a1a1a;
}
.history-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  background: #0a0a0a;
  border-radius: 8px;
  border-left: 3px solid #333;
  gap: 10px;
}
.history-valid { border-left-color: #2ecc71; }
.history-used { border-left-color: #f39c12; }
.history-cancelled { border-left-color: #e74c3c; opacity: 0.6; }
.history-item-left {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.history-func { font-size: 14px; color: #e0e0e0; font-weight: bold; }
.history-meta { font-size: 12px; color: #888; }
.history-uuid { font-size: 11px; color: #444; font-family: monospace; }
.history-item-right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}
.history-status { font-size: 11px; color: #666; text-transform: uppercase; }
.history-time { font-size: 12px; color: #555; }
.reprint-btn {
  background: #1a1a1a;
  border: 1px solid #333;
  border-radius: 6px;
  padding: 6px 10px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
}
.reprint-btn:hover { background: #222; border-color: #c9a227; }
.qty-locked {
  color: #c9a227;
  font-size: 16px;
  font-weight: bold;
  padding: 0 12px;
}

.use-ticket-btn {
  background: #0a1a0a;
  border: 1px solid #2ecc71;
  border-radius: 4px;
  cursor: pointer;
  padding: 2px 6px;
  font-size: 14px;
  transition: background 0.2s;
}
.use-ticket-btn:hover { background: #0d2a0d; }

.cancel-ticket-btn {
  background: #1a1a1a;
  border: 1px solid #333;
  border-radius: 6px;
  padding: 6px 10px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
}
.cancel-ticket-btn:hover { background: #2a0a0a; border-color: #e74c3c; }
.delete-ticket-btn {
  background: #1a1a1a;
  border: 1px solid #333;
  border-radius: 6px;
  padding: 6px 10px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
}
.delete-ticket-btn:hover { background: #2a0a0a; border-color: #c0392b; }

/* ─── Print (hidden on screen) ───── */
.print-area {
  position: fixed;
  left: -9999px;
  top: 0;
}

/* ─── Responsive ─────────────────── */
@media (max-width: 600px) {
  .bol-header { padding: 10px 14px; }
  .header-title { font-size: 16px; }
  .nav-link { font-size: 13px; padding: 6px 8px; }
  .bol-content { padding: 16px 10px; }
  .form-card, .result-card, .stats-card { padding: 20px 14px; }
  .form-row { flex-direction: column; gap: 0; }
  .payment-btn { min-width: 90px; padding: 12px 10px; font-size: 13px; }
  .stats-grid { grid-template-columns: repeat(2, 1fr); }
  .sell-btn { font-size: 16px; padding: 14px; }
  .history-item { flex-direction: column; align-items: flex-start; }
  .history-item-right { width: 100%; justify-content: space-between; }
}
</style>